import { Patient } from "@cur8/rich-entity";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKey as patientKey } from "hooks/queries/usePatientQuery";
import { useAPIClient } from "render/context/APIContext";
import { useReporting } from "render/hooks/useReporting";

type Variables = {
  patientId: string;
  patientNameObject: Patient["name"];
  preferredName: string;
};

function withDisplayName(patient: Patient | undefined, displayName: string) {
  return patient == null
    ? patient
    : { ...patient, name: { ...patient.name, displayName } };
}

export function usePreferredNameMutation() {
  const apiClient = useAPIClient();
  const queryClient = useQueryClient();
  const { logError } = useReporting();

  return useMutation({
    mutationFn({ patientId, patientNameObject, preferredName }: Variables) {
      // Copied rather than written to in place: the caller passes
      // `patient.name` straight out of the patient query, which is the very
      // object React Query has cached.
      const name = {
        ...patientNameObject,
        displayName: preferredName.trim(),
      };

      return apiClient.patient.updatePatient(patientId, {
        name,
      }).result;
    },

    // Written before the request resolves because check-in navigates away on
    // the very next line. The screens it lands on (the header, room
    // assignment, skin scanner) read displayName out of this cache, so
    // waiting for the round-trip would show them the old name — or none at
    // all, for a member who hadn't set one.
    async onMutate({ patientId, preferredName }: Variables) {
      const key = patientKey(patientId);
      // Stop an in-flight fetch from landing on top of the optimistic value.
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<Patient>(key);
      queryClient.setQueryData(key, (old: Patient | undefined) =>
        withDisplayName(old, preferredName.trim())
      );

      return { previous };
    },

    onError(error, { patientId }, context) {
      // Roll back before reporting: logError rethrows, and leaving the
      // optimistic value in place would keep showing every later screen a
      // preferred name the backend never accepted.
      if (context?.previous != null) {
        queryClient.setQueryData(patientKey(patientId), context.previous);
      }
      logError(error);
    },

    // The optimistic value is raw operator input; this is what the backend
    // actually stored, so let it win.
    onSuccess(data, { patientId }) {
      const stored = data?.name?.displayName;
      if (stored == null) {
        return;
      }
      queryClient.setQueryData(
        patientKey(patientId),
        (old: Patient | undefined) => withDisplayName(old, stored)
      );
    },
  });
}
